import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import * as subMatFolderActions from '../../store/subMatFolder';

/**
 * 폴더 또는 파일 이름 바꾸기.
 * file이 있으면 파일 rename (성적서면 pair 대비표 서버에서 연동).
 */
const SubMatFolderRenameDialog = ({
  open,
  folder,
  file,
  folderId,
  onClose,
  onRenamed,
}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);
  const isFile = Boolean(file);

  useEffect(() => {
    if (!open) return;
    if (isFile) {
      setName(file.fileName || '');
    } else if (folder) {
      setName(folder.name || '');
    }
    setErrors([]);
  }, [open, folder, file, isFile]);

  // 열릴 때 이름 전체 블록 선택 → 바로 덮어쓰기 가능
  useEffect(() => {
    if (!open || (!folder && !file)) return undefined;
    const timer = window.setTimeout(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [open, folder, file]);

  const parseApiError = (err) => {
    if (err?.serverResponse) {
      const sr = err.serverResponse;
      if (sr.errors?.length) return sr.errors;
      if (sr.message) return [sr.message];
    }
    return ['이름 바꾸기에 실패했습니다.'];
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setErrors([isFile ? '파일 이름을 입력하세요.' : '폴더 이름을 입력하세요.']);
      return;
    }
    setErrors([]);
    try {
      if (isFile) {
        const fid = folderId || file.folderId;
        if (!fid) {
          setErrors(['폴더 정보가 없습니다.']);
          return;
        }
        await dispatch(subMatFolderActions.renameFile(fid, file.id, trimmed));
        onRenamed?.(fid, file.id);
      } else {
        if (!folder) return;
        await dispatch(subMatFolderActions.modify({
          id: folder.id,
          rootKind: folder.rootKind,
          name: trimmed,
        }));
        onRenamed?.(folder.id);
      }
      onClose();
    } catch (err) {
      setErrors(parseApiError(err));
    }
  };

  if (!folder && !file) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>이름 바꾸기</DialogTitle>
      <DialogContent>
        {errors.map((e, i) => (
          <div key={i} style={{ color: 'red', marginBottom: 8 }}>{e}</div>
        ))}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="새 이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText={isFile && file?.comparisonFile
                ? '연결된 시험성과대비표도 「파일명_성과대비표.pdf」로 함께 바뀝니다.'
                : undefined}
              inputRef={inputRef}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained">확인</Button>
        <Button onClick={onClose}>취소</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubMatFolderRenameDialog;
